import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Button } from "@/components/flexkit/Button";
import { Pill } from "@/components/podkit/status/Pill";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import {
    IntegrationRequirementStatus,
    useIntegrationRequirements,
    type IntegrationRequirement,
} from "@/hooks/use-integration-requirements";
import type { AutomationTemplate } from "@/utils/automation-templates";
import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";

type RequiredIntegrationsDialogProps = {
    open: boolean;
    template: AutomationTemplate | null;
    onClose: () => void;
};

export const RequiredIntegrationsDialog: FC<RequiredIntegrationsDialogProps> = ({ open, template, onClose }) => {
    const { requirements } = useIntegrationRequirements(template?.requiredIntegrations ?? []);
    const navigate = useNavigate();

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const handleConfigureIntegrations = useCallback(() => {
        navigate("/settings/org-integrations");
    }, [navigate]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-md"
                data-track-location={TrackLocations.RequiredIntegrationsDialog}
                data-testid="required-integrations-dialog"
            >
                <DialogHeader>
                    <DialogTitle>Required Integrations</DialogTitle>
                    <DialogDescription>
                        The <span className="font-medium text-content-primary">{template?.title}</span> template
                        requires the following integrations to be configured.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <div className="flex flex-col gap-2">
                        {requirements.map((req) => (
                            <IntegrationRow key={req.name} requirement={req} />
                        ))}
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="required-integrations-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="primary"
                        onClick={handleConfigureIntegrations}
                        data-testid="required-integrations-configure"
                        data-tracking-id="configure-integrations-from-template"
                    >
                        Configure Integrations
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const statusConfig: Record<IntegrationRequirementStatus, { label: string; variant: "success" | "warning" | "danger" }> =
    {
        [IntegrationRequirementStatus.Ready]: { label: "Ready", variant: "success" },
        [IntegrationRequirementStatus.Disabled]: { label: "Disabled", variant: "warning" },
        [IntegrationRequirementStatus.NotCreated]: { label: "Not configured", variant: "danger" },
    };

const IntegrationRow: FC<{ requirement: IntegrationRequirement }> = ({ requirement }) => {
    const { label, variant } = statusConfig[requirement.status];

    return (
        <div
            className="flex items-center justify-between gap-3 rounded-lg border border-border-base bg-surface-glass px-4 py-3"
            data-testid={`integration-row-${requirement.name.toLowerCase()}`}
        >
            <div className="flex items-center gap-3">
                {requirement.definition?.iconUrl && (
                    <img
                        src={requirement.definition.iconUrl}
                        alt={`${requirement.name} icon`}
                        className="h-5 w-5 shrink-0"
                    />
                )}
                <Text className="text-sm font-medium text-content-primary">{requirement.name}</Text>
            </div>
            <Pill size="sm" variant={variant}>
                {label}
            </Pill>
        </div>
    );
};
